import { useEffect, useRef } from 'react';
import styles from './MainLayout.module.scss';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { TopNavigation, SideNavigation, SiteBanner, GlobalSearch } from '@components';
import { Modules } from '@modules';
import { useRecoilValue, useRecoilState } from 'recoil';
import SuspenseLoader from '@pages/SuspenseLoader';
import {
	SideNavOpen,
	IsAdmin,
	JWT,
	UserMenuOpen,
	authenticatedUser,
} from '@state';
import useInitAuthenticatedUser from '@hooks/useInitAuthenticatedUser.ts';
import { useMediaQuery, usePageTitle, useVersionCheck } from '@hooks';

const SAM_API_URL = import.meta.env.VITE_SAM_API_URL;

type LayoutProps = {
	requireAdmin?: boolean;
	initComplete: boolean;
}

const MainLayout = (props:LayoutProps) => {
	const { requireAdmin = false, initComplete } = props;
	const navigate = useNavigate();
	const location = useLocation();
	const [sideNavOpen, setSideNavOpen] = useRecoilState(SideNavOpen);
	const [userMenuOpen, setUserMenuOpen] = useRecoilState(UserMenuOpen);
	const isAdmin = useRecoilValue(IsAdmin);
	const jwt = useRecoilValue(JWT);
	const user = useRecoilValue(authenticatedUser);
	const jwtRef = useRef(jwt);
	const { isMobile } = useMediaQuery();
	const { set } = usePageTitle();
	useInitAuthenticatedUser();
	useVersionCheck();

	useEffect(() => {
		if (requireAdmin && !isAdmin && initComplete) navigate(`/login`, { replace: true });
	}, [requireAdmin, initComplete]);

	useEffect(() => {
		if (!jwt && initComplete) {
			navigate(`/login?backto=${encodeURIComponent(`${location.pathname}${location.search}`)}`, { replace: true });
		}
	}, [jwt, initComplete]);

	useEffect(() => {
		setTimeout(() => {
			if (!isNaN(Number(location.pathname))) {
				set('Ofload', false);
			} else {
				set(location.pathname, false);
			}
		}, 0);
	}, [location]);

	useEffect(() => {
		if (isMobile) {
			setSideNavOpen(false);
		}
	}, [location.pathname])

	useEffect(() => {
		if (isMobile) {
			setSideNavOpen(false);
		}
	}, [isMobile])

	useEffect(() => {
		jwtRef.current = jwt;
	}, [jwt]);

	useEffect(() => {
		if (!isAdmin || !user || !SAM_API_URL) return;
		(window as any).__samWidgetTokenAuth__ = () => `Bearer ${jwtRef.current}`;

		const script = document.createElement('script');
		script.src = `${SAM_API_URL}/js/sam-chat-widget.js`;
		script.setAttribute('data-api-url', SAM_API_URL);
		script.setAttribute('data-token-url', '/api/internal/sam-widget-token');
		document.body.appendChild(script);

		return () => {
			script.remove();
			delete (window as any).__samWidgetTokenAuth__;
		};
	}, [isAdmin, user?.id]);

	return (
		<>
			{!initComplete ?
				<SuspenseLoader /> :
				<div data-testid={'@Ofload-MainLayout'} className={styles.mainLayout} id='mainLayout'>
					<div className={`${styles.blurredbox} ${userMenuOpen ? styles.blurred : ''}`} onClick={() => { setUserMenuOpen(!userMenuOpen) }} />
					<SiteBanner />
					<SideNavigation />
					<TopNavigation />
					{isAdmin && <GlobalSearch />}
					<Modules />
					<div data-testid={'@Ofload-MainLayout-Content'} className={`${styles.content} ${sideNavOpen ? styles.padded : ''}`}>
						<Outlet />
					</div>
				</div>
			}
		</>
	);
}

export default MainLayout;
